import { Fragment, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';

/**
 * Table — the ONE table, ported from views/components/data-table.ejs.
 *
 * It owns the scroll wrapper, the header structure, numeric alignment, the 40px chevron column, the
 * expansion row and the pane cell that paints the tint. A caller supplies columns, rows and optionally a
 * pane per row; it never writes <table>, <thead>, <tr> or the chevron.
 */
export type Column = {
  label?: ReactNode;
  /** Right-aligns the column and its cells — numbers only. */
  num?: boolean;
  /** A pixel width, or a percentage for the column that should take the leftover space. */
  width?: number | string;
  className?: string;
};

export type Cell =
  | ReactNode
  | {
      content: ReactNode;
      className?: string;
      colspan?: number;
      title?: string;
      /**
       * The EJS data-table's per-cell `attrs`: `{ attrs: { style: 'padding-left: 14px; position: relative;' } }`.
       * The list page's caret cell is positioned and padded that way, so a port without it draws the caret in
       * a differently sized column.
       */
      attrs?: Record<string, string | number | undefined>;
      style?: CSSProperties;
    };

export type Row = {
  key?: string | number;
  cells: Cell[];
  rowClass?: string;
  /**
   * Attributes for the <tr>: the EJS tables carry hooks like data-ticker-row="AAPL" and data-lot, and the
   * stylesheet and the browser suite address rows by them. Rendering the same row without them is a row that
   * exists under another name.
   */
  rowAttrs?: Record<string, string | number | boolean | undefined>;
  /** A pane under this row. `open` is controlled by the caller, so the page keeps its own state. */
  pane?: {
    open?: boolean;
    content: ReactNode;
    rowClass?: string;
    paneClass?: string;
    wrap?: boolean;
  /** Pin wrapped rows the way views/layout.ejs's alignWrappedRows() does, without opting into wrap-cells. */
  alignRows?: boolean;
    /** Attributes for the pane row: the EJS detail row is addressed by data-detail-for. */
    rowAttrs?: Record<string, string | number | boolean | undefined>;
    /**
     * The pane's own `<td>` styles. The EJS detail pane passes
     * `cellAttrs: { style: 'background: rgba(0,0,0,0.15); padding: 12px 14px;' }`, and without it the cell
     * keeps the base table-cell padding (7/0/9/0) — the pane's content then starts 41px lower and runs to
     * the cell's edges.
     */
    cellStyle?: CSSProperties;
    cellClass?: string;
    /**
     * 'lazy' renders the pane's content only while the row is open — the EJS list page swaps the fragment in
     * on expand, and a hidden pane that is still markup shows its tables' headers to anything that walks the
     * document. 'always' (the default) keeps it mounted, which the pages whose panes are always present need.
     */
    render?: 'lazy' | 'always';
  };
};

export type TableProps = {
  columns: Column[];
  rows: Row[];
  /** Defaults to 'holdings-table row-hover'; an explicit '' means "no extra classes". */
  tableClass?: string;
  tableId?: string;
  /**
   * Id for the scrolling wrapper. The EJS tables live inside a named div (`#device-requests table`), and the
   * pages' own checks address the wrapper as well as the table.
   */
  wrapperId?: string;
  /** A compact table is the same component at a smaller scale: a totals strip, not a page's table. */
  compact?: boolean;
  /** false removes the chevron column entirely — the row itself opens the pane. */
  chevron?: boolean;
  expandable?: boolean;
  header?: boolean;
  empty?: ReactNode;
  /** Cells that wrap instead of truncating (the scroll wrapper gains `wrap-cells`). */
  wrap?: boolean;
  /** Pin wrapped rows the way views/layout.ejs's alignWrappedRows() does, without opting into wrap-cells. */
  alignRows?: boolean;
  bodyId?: string;
  /**
   * Drag-to-reorder. Off by default: only the list page reorders, and every other table would grow a
   * drag cursor it cannot honour. `onDrop` reports positions in the ROWS array the caller passed, so the
   * page decides what the drop means (and what to persist).
   */
  reorder?: {
    enabled: boolean;
    onDrop: (from: number, to: number, order?: string[]) => void;
    onHover?: (from: number, to: number) => void;
  };
  /** 'single' (default) opens one row at a time; 'multi' lets a page keep several open and says so in
   *  data-expand, which is the hook the gallery's own check reads. */
  expand?: 'single' | 'multi';
  /** The <thead>'s own class: the metrics page's routes table carries `t-body` on the head as well as the table. */
  headClass?: string;
  /** Tables that fill their card instead of scrolling sideways: a two-column list of facts has no 640px floor. */
  fit?: boolean;
  /** A row with a pane opens on click. The page owns WHICH row is open (an uncontrolled table would
   *  forget it on the next refetch, and the EJS table is server-rendered from the page's state). */
  onClickRow?: (key: string) => void;
  className?: string;
  style?: CSSProperties;
};

type CellMeta = {
  content: ReactNode;
  className?: string;
  colspan?: number;
  title?: string;
  attrs: Record<string, string | number | undefined>;
  style?: CSSProperties;
};

const cellOf = (c: Cell): CellMeta => {
  if (c !== null && typeof c === 'object' && !Array.isArray(c) && 'content' in (c as object)) {
    const cell = c as { content: ReactNode; className?: string; colspan?: number; title?: string; attrs?: Record<string, string | number | undefined>; style?: CSSProperties };
    // `attrs` is the EJS shape and may carry a style string; the typed `style` prop wins when both are set.
    const fromAttrs: CSSProperties = {};
    const extra: Record<string, string | number | undefined> = {};
    for (const [k, v] of Object.entries(cell.attrs || {})) {
      if (k === 'style' && typeof v === 'string') {
        for (const part of v.split(';')) {
          const i = part.indexOf(':');
          if (i < 0) continue;
          const key = part.slice(0, i).trim();
          const val = part.slice(i + 1).trim();
          if (!key) continue;
          const camel = key.replace(/-([a-z])/g, (_, ch: string) => ch.toUpperCase());
          (fromAttrs as Record<string, string>)[camel] = val;
        }
      } else {
        extra[k] = v;
      }
    }
    return { ...cell, style: { ...fromAttrs, ...(cell.style || {}) }, attrs: extra };
  }
  return { content: c as ReactNode, attrs: {} };
};

// The index the drag started from. `dataTransfer.getData()` is only readable during `dragstart` and `drop` (the
// browser blanks it in `dragover`), so the live-preview callback — which runs on every dragover — needs the value
// kept somewhere the rows can see. One table reorders at a time, so one module-level slot is enough; it is reset
// on drop and on dragend.
let dragFromIndex: number | null = null;

export function Table({
  columns,
  rows,
  tableClass = 'holdings-table row-hover',
  tableId,
  wrapperId,
  compact,
  chevron,
  expandable = true,
  header = true,
  empty,
  wrap,
  alignRows,
  bodyId,
  onClickRow,
  reorder,
  className,
  style,
  fit,
  headClass,
  expand = 'single',
}: TableProps) {
  const chev = expandable && chevron !== false;
  const tableRef = useRef<HTMLTableElement | null>(null);
  // Live drag order. The preview belongs to the TABLE, not to the page: the EJS table moves the dragged <tr> in
  // the DOM on every dragover, so a page that only heard `onDrop` showed the new order after the release. Pages
  // now get the preview for free and only decide what a drop MEANS (`onDrop(from, to)`).
  const [dragPreview, setDragPreview] = useState<string[] | null>(null);
  const dragPreviewRef = useRef<string[] | null>(null);
  const dragKey = useRef<string | null>(null);
  const keyOf = (r: Row, i: number) => String(r.key ?? i);
  const setPreview = (next: string[] | null) => {
    dragPreviewRef.current = next;
    setDragPreview(next);
  };
  const originalIndexOf = new Map(rows.map((r, i) => [keyOf(r, i), i]));
  const shownRows = dragPreview
    ? (dragPreview.map((k) => rows[originalIndexOf.get(k) ?? -1]).filter(Boolean) as Row[])
    : rows;
  const startDrag = (key: string) => {
    dragKey.current = key;
    dragFromIndex = originalIndexOf.get(key) ?? null;
    setPreview(rows.map((r, i) => keyOf(r, i)));
  };
  const hoverDrag = (targetKey: string) => {
    const key = dragKey.current;
    const order = dragPreviewRef.current;
    if (!key || !order || key === targetKey) return;
    // Positions are looked up in the CURRENT preview, never from the render that produced the handler: once the
    // dragged row has moved, the row under the pointer has a different index than the one it was rendered with,
    // and using that stale index made the preview lag a step behind the pointer.
    const from = order.indexOf(key);
    const to = order.indexOf(targetKey);
    if (from < 0 || to < 0 || from === to) return;
    const next = [...order];
    next.splice(from, 1);
    next.splice(to, 0, key);
    setPreview(next);
    reorder?.onHover?.(from, to);
  };
  const endDrag = () => {
    const key = dragKey.current;
    const order = dragPreviewRef.current;
    const from = dragFromIndex;
    const to = key && order ? order.indexOf(key) : -1;
    dragKey.current = null;
    dragFromIndex = null;
    setPreview(null);
    if (from != null && to >= 0 && to !== from) reorder?.onDrop(from, to, order ?? undefined);
  };

  /**
   * views/layout.ejs's alignWrappedRows(), ported: a row where any cell's text wraps onto a second line has
   * every cell pinned to the top with 7px/7px padding (the layout script writes exactly that inline). Without
   * it a wrapped row keeps the base 7/9 padding and stands 2px taller than the EJS row — the difference the
   * api reference's params table showed.
   */
  useLayoutEffect(() => {
    // Every table, as the page does it: views/layout.ejs runs alignWrappedRows() over the whole document, so a
    // wrapped row is pinned on any EJS table. Gating this on `wrap`/`alignRows` left every other table's wrapped
    // row 2px taller here — the gallery's pane and two-line rows, for instance, and the api reference's params
    // table before it. Cells carrying a visible button are skipped by the rules below, which is what keeps the
    // tax quarterly trigger where the page puts it.
    const table = tableRef.current;
    if (!table) return;

    // ── alignWrappedRows(), ported from views/layout.ejs ────────────────────────────────────────────
    // The layout script runs on every table on the page; the SPA had no equivalent, so a row whose text
    // wraps kept the base 7/9 padding and stood 2px taller than the EJS row (the api reference's params
    // table), and pinning without the layout's rules dropped a label 9px below its row (the tax popover).
    // The rules this keeps, verbatim: rows with no <td> are header rows and are left alone; a cell with a
    // visible button is never pinned (the button defines the row height); line tops within 3px are one
    // line; only cells a previous pass pinned are reset; the padding is the table's own, grown to centre
    // the text block when a button makes the row taller than the wrapped text.
    const inFlowTops = (td: HTMLTableCellElement) => {
      const tops: number[] = [];
      const walk = (n: Node) => {
        if (n.nodeType === Node.ELEMENT_NODE) {
          const pos = getComputedStyle(n as Element).position;
          if (pos === 'absolute' || pos === 'fixed') return;
          (n as Element).childNodes.forEach(walk);
        } else if (n.nodeType === Node.TEXT_NODE && n.textContent && n.textContent.trim()) {
          const rg = document.createRange();
          rg.selectNode(n);
          [...rg.getClientRects()]
            .filter((r) => r.width > 2 && r.height > 4)
            .forEach((r) => tops.push(Math.round(r.top)));
        }
      };
      td.childNodes.forEach(walk);
      return [...new Set(tops)].sort((a, b) => a - b);
    };
    const hasVisibleBtn = (td: HTMLTableCellElement) =>
      [...td.querySelectorAll('button')].some((b) => (b as HTMLElement).offsetHeight > 0);

    const apply = () => {
      table.querySelectorAll('tbody tr').forEach((tr) => {
        const cells = [...tr.children].filter((c): c is HTMLTableCellElement => c.tagName === 'TD');
        if (!cells.length) return;
        cells.forEach((td) => {
          if (!td.dataset.wrapPinned) return;
          delete td.dataset.wrapPinned;
          td.style.verticalAlign = '';
          td.style.paddingTop = '';
          td.style.paddingBottom = '';
        });
        let maxLines = 1;
        let lineH = 0;
        cells.forEach((td) => {
          if (hasVisibleBtn(td)) return;
          const tops = inFlowTops(td);
          const lines: number[] = [];
          tops.forEach((t) => {
            const last = lines[lines.length - 1];
            if (last == null || t - last > 3) lines.push(t);
          });
          if (lines.length > 1 && !lineH) lineH = lines[1] - lines[0];
          if (lines.length > maxLines) maxLines = lines.length;
        });
        if (maxLines <= 1 || !lineH) return;
        const pinned = cells.filter((td) => !hasVisibleBtn(td));
        if (!pinned.length) return;
        const base = getComputedStyle(pinned[0]);
        const padTop = parseFloat(base.paddingTop) || 0;
        let btnH = 0;
        cells.filter(hasVisibleBtn).forEach((td) => {
          const cs = getComputedStyle(td);
          const inner = Math.max(...[...td.querySelectorAll('button')].map((b) => (b as HTMLElement).offsetHeight));
          btnH = Math.max(btnH, inner + (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0));
        });
        const offset = Math.max(padTop, (btnH - maxLines * lineH) / 2);
        pinned.forEach((td) => {
          td.dataset.wrapPinned = '1';
          td.style.verticalAlign = 'top';
          td.style.paddingTop = offset + 'px';
          td.style.paddingBottom = offset + 'px';
        });
      });
    };
    apply();
    window.addEventListener('resize', apply);
    return () => window.removeEventListener('resize', apply);
  }, [rows, columns, wrap, alignRows]);
  const colspan = columns.length + (chev ? 1 : 0);
  // `fit` is the EJS component's style flag (`min-width: 0`), not a class: views/components/data-table.ejs never
  // writes a `fit` class, so neither does this.
  const tableCls = ['data-table', tableClass, compact ? 'compact' : '', className].filter(Boolean).join(' ');

  return (
    <div id={wrapperId} className={wrap ? 'table-scroll wrap-cells' : 'table-scroll'}>
      <table
        id={tableId}
        ref={tableRef}
        className={tableCls}
        data-expand={expandable ? expand : undefined}
        style={{ width: '100%', ...(fit ? { minWidth: 0 } : null), ...style }}
      >
        {header ? (
          <thead className={headClass}>
            <tr>
              {columns.map((c, i) => (
                <th
                  key={i}
                  className={[c.num ? 'num' : '', c.className].filter(Boolean).join(' ') || undefined}
                  style={c.width ? { width: c.width } : undefined}
                >
                  {c.label}
                </th>
              ))}
              {chev ? <th style={{ width: 40 }} /> : null}
            </tr>
          </thead>
        ) : null}
        <tbody id={bodyId}>
          {shownRows.map((r, ri) => {
            // `expandable` decides whether the table has a chevron column, not whether a row may carry a
            // pane: the device log's rows are the affordance themselves and have no chevron to click.
            const open = !!r.pane?.open;
            return (
              <FragmentRow
                key={r.key ?? ri}
                row={r}
                columns={columns}
                chev={chev}
                open={open}
                colspan={colspan}
                expandable={chev}
                onClickRow={onClickRow}
                index={ri}
                wrap={wrap}
                reorder={reorder}
                onDragStartRow={reorder?.enabled ? () => startDrag(String(r.key ?? ri)) : undefined}
                onDragOverRow={reorder?.enabled ? () => hoverDrag(String(r.key ?? ri)) : undefined}
                onDropRow={reorder?.enabled ? () => { hoverDrag(String(r.key ?? ri)); endDrag(); } : undefined}
                onDragEndRow={reorder?.enabled ? () => endDrag() : undefined}
              />
            );
          })}
          {empty && rows.length === 0 ? (
            <tr>
              <td colSpan={colspan} className="muted">
                {empty}
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}

function FragmentRow({
  row: r,
  columns,
  chev,
  open,
  colspan,
  onClickRow,
  index,
  wrap,
  reorder,
  onDragStartRow,
  onDragOverRow,
  onDropRow,
  onDragEndRow,
}: {
  onDragStartRow?: () => void;
  onDragOverRow?: () => void;
  onDropRow?: () => void;
  onDragEndRow?: () => void;
  row: Row;
  columns: Column[];
  chev: boolean;
  open: boolean;
  colspan: number;
  expandable: boolean;
  onClickRow?: (key: string) => void;
  index: number;
  wrap?: boolean;
  /** Pin wrapped rows the way views/layout.ejs's alignWrappedRows() does, without opting into wrap-cells. */
  alignRows?: boolean;
  reorder?: {
    enabled: boolean;
    onDrop: (from: number, to: number, order?: string[]) => void;
    onHover?: (from: number, to: number) => void;
  };
}) {
  const rowClass = [r.rowClass, open ? 'expanded' : ''].filter(Boolean).join(' ');
  const pane = r.pane;
  return (
    <>
      <tr
        {...(r.rowAttrs || {})}
        className={rowClass || undefined}
        // A row can be clickable WITHOUT expanding anything: the tax page's harvest and gain rows are selected
        // by clicking them, and they have no pane. Gating the handler on `pane` made those rows inert.
        // A click that lands on a control belongs to that control. The EJS pages guard exactly this way
        // (`closest('a, button, input, select, label, textarea, form, …')`); without it, clicking a row's pencil
        // ALSO counted as "select this row" — harmless while both could be on, fatal once the actions are
        // exclusive, because the row handler closes the editor the pencil just opened.
        onClick={
          onClickRow
            ? (e) => {
                const t = e.target as HTMLElement | null;
                // `.sel`/`.sel-menu` are the custom picker in this app — a div-based control whose option clicks
                // would otherwise read as a row click and close the very editor they belong to.
                if (t && t.closest('a, button, input, select, label, textarea, form, .sel, .sel-menu, .row-actions, .lot-menu-btn, .info-tip')) return;
                onClickRow(String(r.key ?? ''));
              }
            : undefined
        }
        style={
          reorder?.enabled
            ? { cursor: 'grab' }
            : onClickRow
              ? { cursor: 'pointer' }
              : undefined
        }
        draggable={reorder?.enabled || undefined}
        // The preview lives in Table (see `startDrag`/`hoverDrag`/`endDrag`): a row moves as the pointer passes
        // another row, the way the EJS table's dragover handler does, and the drop reports the move once.
        onDragStart={
          onDragStartRow
            ? (e) => {
                onDragStartRow();
                e.dataTransfer.setData('text/plain', String(index));
              }
            : undefined
        }
        onDragOver={
          onDragOverRow
            ? (e) => {
                e.preventDefault();
                onDragOverRow();
              }
            : undefined
        }
        onDrop={
          onDropRow
            ? (e) => {
                e.preventDefault();
                onDropRow();
              }
            : undefined
        }
        onDragEnd={onDragEndRow}
      >
        {r.cells.map((cell, i) => {
          const c = cellOf(cell);
          const cls = [columns[i]?.num ? 'num' : '', columns[i]?.className, c.className].filter(Boolean).join(' ');
          // The leading space is a real text node between cells. The EJS templates put newlines between their
          // `<td>`s, so a table's textContent reads "10 6 $600.00"; without it React's cells concatenate into
          // "106$600.00" — unreadable to a screen reader that walks the text, and to anything that reads rows
          // as text (the suite asserted `\b6\b` on exactly this).
          return (
            <Fragment key={i}>
              {i > 0 ? ' ' : null}
              <td
                {...(c.attrs || {})}
                className={cls || undefined}
                colSpan={c.colspan}
                title={c.title}
                style={c.style}
                // A wrapping table pins every cell to the top of its row; the flag is how the gallery's own
                // alignment check reads that, and how a page can tell a wrapped row from a plain one.
                data-wrap-pinned={wrap ? '1' : undefined}
              >
                {c.content}
              </td>
            </Fragment>
          );
        })}
        {chev ? (
          <td style={{ position: 'relative', width: 40 }}>
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                paddingRight: 10,
              }}
            >
              <span className="chev" style={{ display: 'inline-flex' }}>
                <svg width="12" height="12" viewBox="0 0 12 12" style={{ display: 'block' }}>
                  <path
                    d="M2 4 L6 8 L10 4"
                    fill="none"
                    stroke="var(--muted)"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </div>
          </td>
        ) : null}
      </tr>
      {/* A pane does not need a chevron column to exist: the row itself can be the affordance. A lazy pane is
          not merely hidden when the row is closed — it is not markup at all, the way the EJS fragment is not. */}
      {pane && (open || pane.render !== 'lazy') ? (
        <tr
          {...(pane.rowAttrs || {})}
          className={['pane-row', pane.rowClass].filter(Boolean).join(' ')}
          style={open ? undefined : { display: 'none' }}
        >
          {/* `paneClass` names the WRAPPER inside the cell, as the EJS data-table does — the cell carries the
              pane's tint and the wrapper is transparent on top of it. Putting the class on the <td> instead made
              ".div-history" resolve to a tinted cell, and the page's own check that the wrapper adds no second
              layer read the tint as a layer. */}
          <td className={['pane-cell', pane.cellClass].filter(Boolean).join(' ')} colSpan={colspan} style={pane.cellStyle}>
            {/* The pane's content exists only while the row is open. A pane that is in the DOM but hidden is
                still MARKUP: its tables' headers count, its "no lots for this ticker" reads to anything that
                walks the document, and the EJS page has no such node until the row is expanded either. */}
            {pane.render === 'lazy' && !open
              ? null
              : pane.wrap === false
                ? pane.content
                : (
                    // `.pane` is the base wrapper; a page's own name is ADDED to it, because the pane styles
                    // (padding, the un-stick rules) hang off `.pane` and a pane that only carries `div-history`
                    // loses them.
                    <div className={['pane', pane.paneClass].filter(Boolean).join(' ')}>{pane.content}</div>
                  )}
          </td>
        </tr>
      ) : null}
    </>
  );
}
